export type Language = "en" | "bg" | "ru";

// Sorting translations - will be merged into properties section

export const languages: { code: Language; name: string; flag: string }[] = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "bg", name: "Български", flag: "🇧🇬" },
  { code: "ru", name: "Русский", flag: "🇷🇺" },
];

const en = {
  nav: {
    home: "Home",
    deals: "Search",
    pricing: "Pricing",
    forAgents: "For Agents",
    blog: "Blog",
    analytics: "Analytics",
    howItWorks: "How It Works",
    signIn: "Log In",
    getStarted: "Get Started",
    startFree: "Start Free",
    dashboard: "Dashboard",
    myFilters: "My Filters",
    myAlerts: "My Alerts",
    signOut: "Sign Out",
    favorites: "Favorites",
  },
  hero: {
    title: "Know the Real Value of Any Property in Bulgaria",
    subtitle:
      "AI analyzes 50,000+ listings to deliver instant valuations, market comparisons, and neighborhood scores.",
    ctaPrimary: "Start Free",
    ctaSecondary: "See Pricing",
    trustLine: "No credit card required • Free search forever",
    stats: {
      properties: "Properties Analyzed",
      avgSavings: "Avg. saved per deal thanks to our algorithm",
      activeAlerts: "Active Alerts",
      citiesCovered: "Cities Covered",
    },
    banners: {
      banner1Title: "Afraid of overpaying for an apartment in Bulgaria?",
      banner1Description: "We'll calculate for free how much a square meter actually costs in your chosen area or city",
      banner1Bonus: "You'll also get a 20% discount on any of our services",
      banner1Cta: "Calculate",
      banner2Title: "Investing in Bulgarian real estate?",
      banner2Description:
        "Get exclusive analytics for free: a heat map of price per m² and real annual price growth (ROI) by districts of Sofia or the coast",
      banner2Bonus: "You'll also get a 20% discount on any of our services",
      banner2Cta: "Get Analytics",
    },
    mockCard: {
      location: "Lozenets, Sofia",
      price: "€128,000",
      area: "78 m²",
      pricePerSqm: "€1,641/m²",
      aiValuation: "AI Valuation",
      belowMarket: "12% below market",
      marketScore: "Market Score",
    },
  },
  marketProblem: {
    title: "The market works against the buyer. We change the rules.",
    description:
      "Traditional brokers protect the seller or their commission. Dealigator works only for you — we reduce the price and guarantee the purity of the deal.",
    standard: {
      title: "Standard",
      seller: "Seller",
      sellerBroker: "Seller's Broker",
      wantHighPrice: "Both want a higher price",
      vs: "VS",
      lonelyBuyer: "Lonely Buyer",
    },
    dealigator: {
      title: "With Dealigator",
      buyer: "Buyer",
      aiLawyerNegotiator: "AI + Lawyer + Negotiator",
      fullProtection: "Full protection of your interests",
      vs: "VS",
      seller: "Seller",
    },
  },
  secretWeapon: {
    title: "We know the real price before negotiations.",
    description: "Our algorithm analyzes 50+ factors and evaluates the property with 97% accuracy.",
    benefit:
      'How does this help you? You go to the viewing with an "ace up your sleeve" — you know exactly how much the property is worth and have arguments to reduce the price.',
  },
  services: {
    title: "Full protection from search to key",
    aiAnalysis: {
      title: "AI Valuation & Analysis",
      description: "We find undervalued properties and show their real value, hidden risks, and rental potential.",
    },
    negotiation: {
      title: "Negotiations & Deal",
      description:
        "Our expert negotiates on your behalf. Goal: reduce the price by 10-15%. If we don't reduce it — the service is free.",
    },
    legal: {
      title: "Legal Security",
      description:
        "We check the property for encumbrances, prepare contracts, and protect your money in an escrow account.",
    },
  },
  battle: {
    title: "Why choose Dealigator?",
    broker: "Regular Broker",
    dealigator: "Dealigator",
    interest: { label: "Whose interest?", broker: "Seller's / Own", ai: "Only Yours (Buyer's)" },
    priceEval: { label: "Price evaluation", broker: '"By eye" / What seller says', ai: "AI Algorithm (97% accuracy)" },
    goalNeg: { label: "Negotiation goal", broker: "Quick deal", ai: "Maximum discount" },
    legalCheck: { label: "Legal check", broker: "Superficial", ai: "Deep (Encumbrances, history)" },
    pricing: { label: "Price", broker: "3-6% commission", ai: "2.5% or fixed fee (depends on the service)" },
  },
  featuredDeals: {
    badge: "New",
    title: "Latest Listings",
    subtitle: "The newest apartments on the market — be the first to see them.",
    viewAll: "View All",
    belowMarket: "below market",
    beds: "beds",
    baths: "baths",
    viewDeal: "View Deal",
  },
  analyticsPreview: {
    badge: "Smart Insights",
    title: "Your Personal Analytics Platform",
    subtitle:
      "Deep market insights, price predictions, and trend analysis to help you make informed investment decisions.",
    features: {
      priceAnalytics: {
        title: "Price Analytics",
        description: "Historical trends and price forecasting",
      },
      heatMaps: {
        title: "Heat Maps",
        description: "Visualize hot zones and emerging areas",
      },
      roiCalculator: {
        title: "ROI Calculator",
        description: "Estimate your investment returns",
      },
      dealAlerts: {
        title: "Deal Alerts",
        description: "Instant notifications for matching deals",
      },
    },
    exploreButton: "Explore Analytics",
    marketOverview: "Sofia Market Overview",
    pricePerSqm: "Price/sqm",
    listings: "Listings",
    avgDays: "Avg. Days",
    newDealFound: "New Deal Found!",
    matchesFilters: "Matches your filters",
  },
  howItWorks: {
    badge: "Simple Process",
    title: "How Dealigator Works",
    subtitle: "Three simple steps to finding your perfect property deal.",
    steps: {
      collect: {
        step: "01",
        title: "We Collect Data",
        description: "Our system aggregates listings from 20+ sources across Bulgaria, updating every hour.",
      },
      analyze: {
        step: "02",
        title: "AI Analyzes Deals",
        description: "Machine learning models evaluate each property against market data to find underpriced gems.",
      },
      alert: {
        step: "03",
        title: "You Get Alerted",
        description: "Receive instant notifications when properties matching your criteria appear below market value.",
      },
    },
  },
  footer: {
    cta: {
      title: "Ready to Find Your Next Deal?",
      subtitle: "Join thousands of investors already using Dealigator to discover undervalued properties.",
      emailPlaceholder: "Enter your email",
      button: "Get Started Free",
    },
    brand: {
      description:
        "The smart way to buy a home. AI valuation, legal protection and professional price negotiation. We work only for you, not for the seller.",
    },
    platform: {
      title: "Platform",
      search: "Search Deals",
      analytics: "Analytics",
      alerts: "Price Alerts",
      api: "API Access",
    },
    resources: {
      title: "Resources",
      blog: "Blog",
      guides: "Market Guides",
      support: "Support",
      pricing: "Pricing",
    },
    contact: {
      title: "Contact",
      location: "Sofia, Bulgaria",
      feedback: "Feedback Form",
    },
    legal: {
      rights: "All rights reserved.",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
    },
  },
  contact: {
    back: "Back",
    title: "Contact Us",
    subtitle: "We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
    formTitle: "Send a Message",
    formDescription: "Fill out the form below and we'll get back to you shortly.",
    name: "Name",
    namePlaceholder: "Your name",
    email: "Email",
    emailPlaceholder: "your@email.com",
    subject: "Subject",
    subjectPlaceholder: "How can we help?",
    message: "Message",
    messagePlaceholder: "Tell us more about your inquiry...",
    submit: "Send Message",
    sending: "Sending...",
    successTitle: "Message Sent",
    successDescription: "Thank you for your message. We'll get back to you soon!",
    errors: {
      nameMin: "Name must be at least 2 characters",
      nameMax: "Name must be less than 100 characters",
      invalidEmail: "Please enter a valid email address",
      emailMax: "Email must be less than 255 characters",
      subjectMin: "Subject must be at least 3 characters",
      subjectMax: "Subject must be less than 200 characters",
      messageMin: "Message must be at least 10 characters",
      messageMax: "Message must be less than 2000 characters",
      captchaRequired: "Captcha required",
      captchaDescription: "Please complete the captcha verification",
      submitFailed: "Submission failed",
      tryAgain: "Please try again later",
    },
  },
  auth: {
    backToHome: "Back to Home",
    signIn: "Sign In",
    signUp: "Create Account",
    signInDesc: "Sign in to access your saved filters and alerts",
    signUpDesc: "Register to track the best deals",
    name: "Name",
    namePlaceholder: "Your name",
    email: "Email",
    emailPlaceholder: "email@example.com",
    password: "Password",
    loading: "Loading...",
    or: "or",
    continueWithGoogle: "Continue with Google",
    noAccount: "Don't have an account?",
    haveAccount: "Already have an account?",
    invalidEmail: "Please enter a valid email",
    passwordMin: "Password must be at least 6 characters",
    nameMin: "Name must be at least 2 characters",
    loginError: "Login Error",
    invalidCredentials: "Invalid email or password",
    error: "Error",
    welcomeBack: "Welcome back!",
    signedIn: "Successfully logged in",
    userExists: "User already exists",
    userExistsDesc: "This email is already registered. Try logging in.",
    registrationError: "Registration Error",
    registrationSuccess: "Registration successful",
    accountCreated: "Account created. Welcome!",
    somethingWrong: "Something went wrong. Please try again.",
    googleError: "Failed to sign in with Google",
    confirmEmail: "Confirm your email",
    confirmEmailSent: "We sent a confirmation link to",
    confirmEmailDesc: "Please check your inbox and click the link to activate your account.",
    checkSpam: "Check your spam folder if you don't see the email.",
    backToLogin: "Back to login",
  },
  dashboard: {
    back: "Back",
    signOut: "Sign Out",
    title: "Dashboard",
    tabs: {
      filters: "Saved Filters",
      alerts: "Alerts",
      profile: "Profile",
    },
    filters: {
      title: "Saved Filters",
      description: "Manage your saved property search filters",
      create: "Create Filter",
      noFilters: "No saved filters",
      noFiltersDesc: "Create your first search filter to start tracking properties.",
      location: "Location",
      priceRange: "Price Range",
      area: "Area",
      type: "Type",
      rooms: "rooms",
      edit: "Edit",
      delete: "Delete",
      filterName: "Filter Name",
      filterNamePlaceholder: "e.g. Sofia Center Apartments",
      locationLabel: "Location",
      locationPlaceholder: "City or neighborhood",
      minPrice: "Min Price (€)",
      maxPrice: "Max Price (€)",
      minArea: "Min Area (m²)",
      maxArea: "Max Area (m²)",
      propertyType: "Property Type",
      selectType: "Select type",
      apartment: "Apartment",
      house: "House",
      studio: "Studio",
      commercial: "Commercial",
      land: "Land",
      roomsLabel: "Rooms",
      saveFilter: "Save Filter",
      cancel: "Cancel",
    },
    alerts: {
      title: "My Alerts",
      description: "Configure alerts for new deals",
      create: "Create Alert",
      noAlerts: "No alerts configured",
      noAlertsDesc: "Create an alert to get notified about new deals matching your criteria.",
      active: "Active",
      linkedFilter: "Linked filter",
      minScore: "Min deal score",
      maxDeviation: "Max market deviation",
      notifications: "Notifications",
      email: "Email",
      push: "Push",
      delete: "Delete",
      alertName: "Alert Name",
      alertNamePlaceholder: "e.g. Hot deals in Sofia",
      selectFilter: "Linked Filter (optional)",
      noFilter: "No filter",
      minDealScore: "Minimum Deal Score",
      maxPriceDeviation: "Max Price vs Market (%)",
      notifyEmail: "Email Notifications",
      notifyPush: "Push Notifications",
      saveAlert: "Save Alert",
      cancel: "Cancel",
    },
    profile: {
      title: "Profile Settings",
      description: "Manage your account information",
      fullName: "Full Name",
      fullNamePlaceholder: "Your name",
      email: "Email",
      emailPlaceholder: "Your email",
      saveChanges: "Save Changes",
      statistics: "Statistics",
      savedFilters: "Saved Filters",
      activeAlerts: "Active Alerts",
      profileUpdated: "Profile updated",
      profileUpdatedDesc: "Your profile has been successfully updated.",
      updateError: "Error",
      updateErrorDesc: "Failed to update profile.",
    },
    toast: {
      filterCreated: "Filter created",
      filterCreatedDesc: "Your filter has been saved.",
      filterDeleted: "Filter deleted",
      filterDeletedDesc: "The filter has been removed.",
      alertCreated: "Alert created",
      alertCreatedDesc: "You will receive notifications for matching deals.",
      alertDeleted: "Alert deleted",
      alertDeletedDesc: "The alert has been removed.",
      alertUpdated: "Alert updated",
      alertUpdatedDesc: "Alert status has been changed.",
      error: "Error",
      errorDesc: "An error occurred. Please try again.",
    },
  },
  properties: {
    title: "Property Search",
    subtitle: "Find the best deals on the market",
    filtersTitle: "Search Filters",
    searchLabel: "Search by keywords",
    searchPlaceholder: "District, description, address...",
    cityLabel: "City",
    cityPlaceholder: "City",
    allCities: "All cities",
    propertyTypeLabel: "Property Type",
    allTypes: "All types",
    roomsLabel: "Number of Rooms",
    allRooms: "Any",
    roomsSuffix: "rooms",
    areaLabel: "Area (m²)",
    priceLabel: "Price (€)",
    pricePerSqmLabel: "Price per m² (€)",
    from: "From",
    to: "To",
    minPrice: "Min price",
    maxPrice: "Max price",
    minArea: "Min m²",
    maxArea: "Max m²",
    searchButton: "Search",
    reset: "Reset",
    noResults: "No properties found",
    showing: "Showing",
    of: "of",
    sortLabel: "Sort:",
    sortBest: "Best",
    sortNew: "New",
    sortMore: "Sort",
    sortPriceAsc: "Price ↑",
    sortPriceDesc: "Price ↓",
    sortAiValuation: "AI Valuation",
    sortArea: "Area",
    sortPricePerSqm: "Price/m²",
    sortHot: "Hot Deals 🔥",
    sortHotTooltip: "Available only for Apartments",
    advancedFilters: "Advanced Filters",
    advancedActive: "Active",
    floorLabel: "Floor",
    constructionYearLabel: "Construction Year",
    buildingMaterialsLabel: "Building Type",
    buildingMaterialsAll: "All",
    gasLabel: "Gas",
    gasOnly: "Gas only",
    centralHeatingLabel: "Central Heating",
    centralHeatingOnly: "Central heating only",
    selectApartments: 'Select type "Apartments"',
  },
  propertyDetail: {
    detailedAnalysis: "Detailed Analysis",
    getDetailedAnalysis: "Get Detailed Analysis — €5",
    backToSearch: "Back to search",
    notFound: "Property not found",
    description: "Description",
    characteristics: "Characteristics",
    pricePerSqm: "Price/m²",
    area: "Area",
    bedrooms: "Rooms",
    propertyType: "Property Type",
    floor: "Floor",
    construction: "Construction",
    buildingMaterials: {
      brick: "Brick",
      panel: "Panel",
      monolith: "Monolith",
      epk: "EPK",
      other: "Other",
    },
    daysOnMarket: "Days on market",
    today: "Today",
    day: "day",
    days: "days",
    viewOriginal: "View on source",
    save: "Save",
    potentialSavings: "Potential savings",
    listingPrice: "Listing price",
    aiValuation: "AI Valuation",
    priceDeviation: "Price deviation",
    similarProperties: "Similar properties",
    utilityCost: "Utility costs",
    priceComparison: "vs District avg",
    vsAvg: "vs avg",
    priceTrend: "Price trend in district",
    last6Months: "Last 6 months",
    districtAvg: "District avg",
    location: "Location",
    openMaps: "Open in Maps",
    noLocation: "Location not available",
    contact: "Contact",
    ownerContacts: "Owner Contacts",
    name: "Name",
    phone: "Phone",
    email: "Email",
    district: "District",
    noContactInfo: "No contact information available",
    moreDetails: "More details",
    districtRatings: {
      traffic: "Traffic",
      publicTransport: "Public Transport",
      shops: "Shops",
      schools: "Schools",
    },
  },
  propertyCard: {
    aiEstimate: "AI Est",
    goodDeal: "Good Deal",
    overpriced: "Overpriced",
    belowMarket: "below market",
    aboveMarket: "above market",
    fairPrice: "fair price",
    significantlyBelow: "Significantly below market price",
    significantlyAbove: "Significantly above market price",
    rooms: "rooms",
    types: {
      apartments: "Apartment",
      house: "House",
      studio: "Studio",
      garage: "Garage",
      land: "Land",
      commercial: "Commercial",
      other: "Property",
    },
  },
  pricing: {
    title: "Pricing",
    subtitle: "Choose the plan that fits your needs",
    free: {
      name: "Free",
      price: "€0",
      period: "",
      description: "Basic access to the platform",
      features: ["Property search", "Basic listings", "General Market Score"],
      cta: "Get Started",
    },
    perProperty: {
      name: "Detailed Analysis",
      price: "€5",
      period: "per property",
      description: "One-time in-depth report for a specific property",
      features: ["Full AI valuation", "Price vs. market comparison", "Neighborhood analytics", "PDF report export"],
      cta: "Buy Analysis",
    },
    pro: {
      name: "Pro",
      price: "€59",
      period: "/month",
      description: "For agents and small agencies",
      features: ["Unlimited reports", "Smart alerts", "Data export", "Priority support", "API access"],
      cta: "Apply",
    },
    enterprise: {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For developers, funds, and large agencies",
      features: [
        "Custom integrations",
        "Dedicated account manager",
        "Bulk data access",
        "White-label options",
        "SLA guarantee",
      ],
      cta: "Contact Us",
    },
    popular: "Most Popular",
  },
  dataBar: {
    listings: "Listings Analyzed",
    neighborhoods: "Neighborhoods Scored",
    updated: "Updated Daily",
    platform: "EU-Based Platform",
  },
  howItWorksNew: {
    title: "How It Works",
    subtitle: "From search to decision in 3 steps",
    step1Title: "Find Properties",
    step1Desc: "Search across all major Bulgarian listing sites in one place. Filter by city, price, type, and more.",
    step2Title: "Get AI Valuation",
    step2Desc:
      "Instant market value estimate, price comparison with similar properties, neighborhood analytics — all in one report.",
    step3Title: "Make Data-Driven Decisions",
    step3Desc: "Know if you're overpaying. See the real market picture before you negotiate.",
  },
  productFeatures: {
    title: "Built for Everyone in Real Estate",
    subtitle: "Whether you're buying your first apartment or managing a portfolio of 500 properties",
    buyers: {
      title: "For Buyers & Investors",
      features: [
        "Free property search across Bulgaria",
        "AI-powered valuation for €5 per property",
        "Market Score for every listing",
        "Price trend charts and comparables",
        "Neighborhood safety & infrastructure data",
      ],
      cta: "Start Free",
    },
    agents: {
      title: "For Agents & Agencies",
      features: [
        "Unlimited AI reports with Pro plan",
        "Client-ready PDF exports",
        "Comparative market analysis",
        "Smart alerts for new listings",
        "Priority support",
      ],
      cta: "Try Pro Free for 14 Days",
    },
    enterprise: {
      title: "For Enterprise",
      features: [
        "API access for bulk valuations",
        "White-label reports with your branding",
        "Custom data integrations",
        "Dedicated account manager",
        "SLA guarantee",
      ],
      cta: "Contact Us",
    },
  },
  pricingTeaser: {
    title: "Simple, Transparent Pricing",
    subtitle: "Start free. Pay only when you need more.",
    free: { name: "Free", desc: "Search & basic data", cta: "Start Free" },
    report: { name: "AI Report", suffix: "/ property", desc: "Full valuation + market analysis", cta: "Try It" },
    pro: { suffix: "/ month", desc: "Unlimited reports + smart tools", cta: "Start Pro Trial" },
    enterprise: "Enterprise? Let's talk.",
    enterpriseLink: "Contact us for custom API, white-label, and bulk data solutions.",
  },
  faq: {
    title: "Frequently Asked Questions",
    subtitle: "Everything you need to know about Dealigator",
    items: [
      {
        q: "What data does Dealigator use?",
        a: "We aggregate listings from all major Bulgarian real estate platforms, combining them with neighborhood data, transaction history, and proprietary AI models to estimate market value.",
      },
      {
        q: "How accurate is the AI valuation?",
        a: "Our model is trained on historical transaction data and continuously updated. While no automated valuation is perfect, Dealigator provides a reliable market estimate to help you negotiate from a position of knowledge.",
      },
      {
        q: "Do I need to create an account?",
        a: "Basic search is available without registration. To purchase AI reports or subscribe to Pro, you'll need a free account.",
      },
      {
        q: "Is my data secure?",
        a: "Yes. We're an EU-based platform, fully compliant with GDPR. We don't share your data with third parties.",
      },
      {
        q: "I'm a real estate agent. Is there a plan for agencies?",
        a: "Yes! Our Pro plan is perfect for individual agents, and Enterprise plans offer API access, white-label reports, and bulk pricing for agencies. Contact us for details.",
      },
    ],
  },
  finalCta: {
    title: "Ready to See the Real Market?",
    subtitle: "Start with a free search. Get your first AI valuation for €5.",
  },
  servicePage: {
    pageTitle: "Services",
    backToServices: "Back to services",
    details: "Details",
    order: "Order",
    consultation: "Consultation",
    addToCart: "Add to cart",
    alreadyInCart: "Already in cart",
    addedToCart: "Added to cart",
    mostPopular: "Most Popular",
    cart: "Cart",
    cartEmpty: "Your cart is empty",
    browseServices: "Browse services",
    name: "Name *",
    email: "Email *",
    phone: "Phone",
    notes: "Notes",
    paymentNote: "* Payment is made after discussing the details with our team.",
    confirmOrder: "Confirm order",
    cancel: "Cancel",
    orderAccepted: "Order accepted!",
    orderAcceptedDesc: "We will contact you by email as soon as possible to discuss the details.",
    ok: "OK",
    fillNameEmail: "Please fill in name and email",
    orderError: "Error placing order",
    waitSecurity: "Please wait for the security check",
    cartOrderNote: "Order from cart",
    consultationBtn: "Service Consultation",
    consultationTitle: "Service Consultation",
    consultationName: "Name *",
    consultationEmail: "Email *",
    consultationPhone: "Phone",
    consultationMessage: "Your question *",
    consultationSubmit: "Send",
    consultationSuccess: "Success!",
    consultationSuccessDesc: "We will contact you soon.",
    consultationError: "Error",
    consultationErrorDesc: "Please try again.",
  },
};

const bg = {
  nav: {
    home: "Начало",
    deals: "Търсене",
    pricing: "Тарифи",
    forAgents: "За агенти",
    blog: "Блог",
    analytics: "Анализи",
    howItWorks: "Как работи",
    signIn: "Вход",
    getStarted: "Започнете",
    startFree: "Започни безплатно",
    dashboard: "Табло",
    myFilters: "Моите филтри",
    myAlerts: "Моите известия",
    signOut: "Изход",
    favorites: "Избрани",
  },
  hero: {
    title: "Разберете реалната стойност на всеки имот в България",
    subtitle: "ИИ анализира 50 000+ обяви: моментална оценка, сравнение с пазара, скоринг на районите.",
    ctaPrimary: "Започни безплатно",
    ctaSecondary: "Виж тарифи",
    trustLine: "Без кредитна карта • Безплатно търсене завинаги",
    stats: {
      properties: "Анализирани имоти",
      avgSavings: "Средно спестени пари на една сделка благодарение на нашия алгоритъм",
      activeAlerts: "Активни известия",
      citiesCovered: "Покрити градове",
    },
    banners: {
      banner1Title: "Страхувате се, че ще надплатите за апартамент в България?",
      banner1Description:
        "Безплатно ще изчислим колко всъщност струва квадратен метър в избрания от Вас район или град",
      banner1Bonus: "Също така ще получите отстъпка -20% на всяка наша услуга",
      banner1Cta: "Изчисли",
      banner2Title: "Инвестирате в българска недвижимост?",
      banner2Description:
        "Безплатно получете закрита аналитика: топлинна карта на стойността на м² и реален годишен прираст на цените (ROI) по райони на София или крайбрежието",
      banner2Bonus: "Също така ще получите отстъпка -20% на всяка наша услуга",
      banner2Cta: "Получи аналитика",
    },
    mockCard: {
      location: "Лозенец, София",
      price: "€128 000",
      area: "78 м²",
      pricePerSqm: "€1 641/м²",
      aiValuation: "AI Оценка",
      belowMarket: "12% под пазара",
      marketScore: "Пазарен рейтинг",
    },
  },
  marketProblem: {
    title: "Пазарът работи срещу купувача. Ние променяме правилата.",
    description:
      "Обикновените брокери защитават продавача или комисионната си. Dealigator работи само за Вас — ние сваляме цената и гарантираме чистотата на сделката.",
    standard: {
      title: "Стандарт",
      seller: "Продавач",
      sellerBroker: "Брокер на продавача",
      wantHighPrice: "И двамата искат по-висока цена",
      vs: "VS",
      lonelyBuyer: "Самостоятелен купувач",
    },
    dealigator: {
      title: "С Dealigator",
      buyer: "Купувач",
      aiLawyerNegotiator: "AI + Юрист + Преговарящо лице",
      fullProtection: "Пълна защита на вашите интереси",
      vs: "VS",
      seller: "Продавач",
    },
  },
  secretWeapon: {
    title: "Знаем реалната цена преди преговорите.",
    description: "Нашият алгоритъм анализира над 50 фактора и оценява имота с точност 97%.",
    benefit:
      'Как ще Ви помогне това? Вие отивате на оглед с "асо в ръкава" — знаете реалната стойност на жилището и имате аргументи да свалите цената',
  },
  services: {
    title: "Пълна защита от търсенето до ключа",
    aiAnalysis: {
      title: "AI Оценка & Анализ",
      description:
        "Намираме подценени имоти и показваме реалната им стойност, скритите рискове и потенциала за отдаване под наем.",
    },
    negotiation: {
      title: "Преговори & Сделка",
      description:
        "Наш експерт преговаря вместо Вас. Целта: да свалим цената с 10-15%. Ако не свалим цената — услугата за преговори е безплатна.",
    },
    legal: {
      title: "Правна сигурност",
      description: "Проверяваме имота за тежести, подготвяме договорите и защитаваме парите Ви в доверителна сметка.",
    },
  },
  battle: {
    title: "Защо да изберете Dealigator?",
    broker: "Обикновен брокер",
    dealigator: "Dealigator",
    interest: { label: "Чий интерес защитава?", broker: "На продавача / Своя", ai: "Само Вашия (на Купувача)" },
    priceEval: {
      label: "Оценка на цената",
      broker: "По преценка на продавача",
      ai: "AI Алгоритъм (97% точност)",
    },
    goalNeg: { label: "Цел на преговорите", broker: "Бърза сделка", ai: "Максимална отстъпка" },
    legalCheck: { label: "Правна проверка", broker: "Повърхностна", ai: "Дълбока (Тежести, история)" },
    pricing: { label: "Цена", broker: "3-6% комисионна", ai: "2.5% или фиксирана такса (в зависимост от услугата)" },
  },
  featuredDeals: {
    badge: "Ново",
    title: "Последни обяви",
    subtitle: "Най-новите апартаменти на пазара — бъдете първи, които ги виждат.",
    viewAll: "Виж всички",
    belowMarket: "под пазарната",
    beds: "спални",
    baths: "бани",
    viewDeal: "Виж оферта",
  },
  analyticsPreview: {
    badge: "Умни анализи",
    title: "Вашата лична аналитична платформа",
    subtitle:
      "Задълбочени пазарни анализи, прогнози за цени и анализ на тенденции за информирани инвестиционни решения.",
    features: {
      priceAnalytics: {
        title: "Ценови анализи",
        description: "Исторически тенденции и прогнози",
      },
      heatMaps: {
        title: "Топлинни карти",
        description: "Визуализирайте горещите зони",
      },
      roiCalculator: {
        title: "ROI калкулатор",
        description: "Оценете възвръщаемостта",
      },
      dealAlerts: {
        title: "Известия за сделки",
        description: "Моментални известия за подходящи сделки",
      },
    },
    exploreButton: "Разгледай анализите",
    marketOverview: "Преглед на пазара в София",
    pricePerSqm: "Цена/кв.м",
    listings: "Обяви",
    avgDays: "Средно дни",
    newDealFound: "Нова сделка!",
    matchesFilters: "Съвпада с филтрите ви",
  },
  howItWorks: {
    badge: "Прост процес",
    title: "Как работи Dealigator",
    subtitle: "Три лесни стъпки до перфектната имотна сделка.",
    steps: {
      collect: {
        step: "01",
        title: "Събираме данни",
        description: "Нашата система събира обяви от 20+ източника в България, актуализирайки всеки час.",
      },
      analyze: {
        step: "02",
        title: "AI анализира",
        description: "Машинно обучение оценява всеки имот спрямо пазарни данни за подценени находки.",
      },
      alert: {
        step: "03",
        title: "Получавате известие",
        description:
          "Получавайте моментални известия когато имоти по вашите критерии се появят под пазарната стойност.",
      },
    },
  },
  footer: {
    cta: {
      title: "Готови за следващата сделка?",
      subtitle:
        "Присъединете се към хиляди инвеститори, които вече използват Dealigator за откриване на подценени имоти.",
      emailPlaceholder: "Въведете имейл",
      button: "Започнете безплатно",
    },
    brand: {
      description:
        "Интелигентният начин да купите дом. AI оценка, юридическа защита и професионално договаряне на цена. Ние работим само за Вас, не за продавача.",
    },
    platform: {
      title: "Платформа",
      search: "Търсене на оферти",
      analytics: "Анализи",
      alerts: "Ценови известия",
      api: "API достъп",
    },
    resources: {
      title: "Ресурси",
      blog: "Блог",
      guides: "Пазарни ръководства",
      support: "Поддръжка",
      pricing: "Цени",
    },
    contact: {
      title: "Контакти",
      location: "София, България",
      feedback: "Форма за обратна връзка",
    },
    legal: {
      rights: "Всички права запазени.",
      privacy: "Политика за поверителност",
      terms: "Условия за ползване",
    },
  },
  contact: {
    back: "Назад",
    title: "Свържете се с нас",
    subtitle: "Ще се радваме да ви чуем. Изпратете ни съобщение и ще отговорим възможно най-скоро.",
    formTitle: "Изпратете съобщение",
    formDescription: "Попълнете формуляра по-долу и ще се свържем с вас скоро.",
    name: "Име",
    namePlaceholder: "Вашето име",
    email: "Имейл",
    emailPlaceholder: "your@email.com",
    subject: "Тема",
    subjectPlaceholder: "Как можем да помогнем?",
    message: "Съобщение",
    messagePlaceholder: "Разкажете ни повече за вашето запитване...",
    submit: "Изпрати съобщение",
    sending: "Изпращане...",
    successTitle: "Съобщението е изпратено",
    successDescription: "Благодарим ви за съобщението. Ще се свържем с вас скоро!",
    errors: {
      nameMin: "Името трябва да е поне 2 символа",
      nameMax: "Името трябва да е по-малко от 100 символа",
      invalidEmail: "Моля, въведете валиден имейл адрес",
      emailMax: "Имейлът трябва да е по-малко от 255 символа",
      subjectMin: "Темата трябва да е поне 3 символа",
      subjectMax: "Темата трябва да е по-малка от 200 символа",
      messageMin: "Съобщението трябва да е поне 10 символа",
      messageMax: "Съобщението трябва да е по-малко от 2000 символа",
      captchaRequired: "Изисква се captcha",
      captchaDescription: "Моля, попълнете captcha проверката",
      submitFailed: "Грешка при изпращане",
      tryAgain: "Моля, опитайте отново по-късно",
    },
  },
  auth: {
    backToHome: "Към началото",
    signIn: "Вход",
    signUp: "Регистрация",
    signInDesc: "Влезте за достъп до запазените филтри и известия",
    signUpDesc: "Регистрирайте се за проследяване на най-добрите сделки",
    name: "Име",
    namePlaceholder: "Вашето име",
    email: "Имейл",
    emailPlaceholder: "email@example.com",
    password: "Парола",
    loading: "Зареждане...",
    or: "или",
    continueWithGoogle: "Продължи с Google",
    noAccount: "Нямате акаунт?",
    haveAccount: "Вече имате акаунт?",
    invalidEmail: "Моля въведете валиден имейл",
    passwordMin: "Паролата трябва да е поне 6 символа",
    nameMin: "Името трябва да е поне 2 символа",
    loginError: "Грешка при вход",
    invalidCredentials: "Невалиден имейл или парола",
    error: "Грешка",
    welcomeBack: "Добре дошли!",
    signedIn: "Успешен вход",
    userExists: "Потребителят съществува",
    userExistsDesc: "Този имейл вече е регистриран. Опитайте вход.",
    registrationError: "Грешка при регистрация",
    registrationSuccess: "Успешна регистрация",
    accountCreated: "Акаунтът е създаден. Добре дошли!",
    somethingWrong: "Нещо се обърка. Моля опитайте отново.",
    googleError: "Неуспешен вход с Google",
    confirmEmail: "Потвърдете вашия имейл",
    confirmEmailSent: "Изпратихме линк за потвърждение на",
    confirmEmailDesc: "Моля проверете входящата си поща и кликнете върху линка, за да активирате акаунта си.",
    checkSpam: "Проверете папката за спам, ако не виждате имейла.",
    backToLogin: "Обратно към входа",
  },
  dashboard: {
    back: "Назад",
    signOut: "Изход",
    title: "Табло",
    tabs: {
      filters: "Запазени филтри",
      alerts: "Известия",
      profile: "Профил",
    },
    filters: {
      title: "Запазени филтри",
      description: "Управлявайте запазените филтри за търсене",
      create: "Създай филтър",
      noFilters: "Няма запазени филтри",
      noFiltersDesc: "Създайте първия си филтър за проследяване на имоти.",
      location: "Локация",
      priceRange: "Ценови диапазон",
      area: "Площад",
      type: "Тип",
      rooms: "стаи",
      edit: "Редактирай",
      delete: "Изтрий",
      filterName: "Име на филтъра",
      filterNamePlaceholder: "напр. Апартаменти център София",
      locationLabel: "Локация",
      locationPlaceholder: "Град или квартал",
      minPrice: "Мин цена (€)",
      maxPrice: "Макс цена (€)",
      minArea: "Мин площад (м²)",
      maxArea: "Макс площад (м²)",
      propertyType: "Тип имот",
      selectType: "Изберете тип",
      apartment: "Апартамент",
      house: "Къща",
      studio: "Студио",
      commercial: "Търговски",
      land: "Парцел",
      roomsLabel: "Стаи",
      saveFilter: "Запази филтър",
      cancel: "Отказ",
    },
    alerts: {
      title: "Моите известия",
      description: "Конфигурирайте известия за нови сделки",
      create: "Създай известие",
      noAlerts: "Няма конфигурирани известия",
      noAlertsDesc: "Създайте известие за нови сделки по вашите критерии.",
      active: "Активно",
      linkedFilter: "Свързан филтър",
      minScore: "Мин скор на сделка",
      maxDeviation: "Макс пазарно отклонение",
      notifications: "Известия",
      email: "Имейл",
      push: "Push",
      delete: "Изтрий",
      alertName: "Име на известие",
      alertNamePlaceholder: "напр. Горещи сделки в София",
      selectFilter: "Свързан филтър (опционално)",
      noFilter: "Без филтър",
      minDealScore: "Минимален скор на сделка",
      maxPriceDeviation: "Макс цена спрямо пазар (%)",
      notifyEmail: "Имейл известия",
      notifyPush: "Push известия",
      saveAlert: "Запази известие",
      cancel: "Отказ",
    },
    profile: {
      title: "Настройки на профила",
      description: "Управлявайте информацията за акаунта",
      fullName: "Пълно име",
      fullNamePlaceholder: "Вашето име",
      email: "Имейл",
      emailPlaceholder: "Вашият имейл",
      saveChanges: "Запази промените",
      statistics: "Статистика",
      savedFilters: "Запазени филтри",
      activeAlerts: "Активни известия",
      profileUpdated: "Профилът е обновен",
      profileUpdatedDesc: "Профилът ви беше успешно обновен.",
      updateError: "Грешка",
      updateErrorDesc: "Неуспешно обновяване на профила.",
    },
    toast: {
      filterCreated: "Филтърът е създаден",
      filterCreatedDesc: "Вашият филтър е запазен.",
      filterDeleted: "Филтърът е изтрит",
      filterDeletedDesc: "Филтърът беше премахнат.",
      alertCreated: "Известието е създадено",
      alertCreatedDesc: "Ще получавате известия за съвпадащи сделки.",
      alertDeleted: "Известието е изтрито",
      alertDeletedDesc: "Известието беше премахнато.",
      alertUpdated: "Известието е обновено",
      alertUpdatedDesc: "Статусът на известието е променен.",
      error: "Грешка",
      errorDesc: "Възникна грешка. Моля опитайте отново.",
    },
  },
  properties: {
    title: "Търсене на имоти",
    subtitle: "Намерете най-добрите оферти на пазара",
    filtersTitle: "Филтри за търсене",
    searchLabel: "Търсене по ключови думи",
    searchPlaceholder: "Квартал, описание, адрес...",
    cityLabel: "Град",
    cityPlaceholder: "Град",
    allCities: "Всички градове",
    propertyTypeLabel: "Тип имот",
    allTypes: "Всички типове",
    roomsLabel: "Брой стаи",
    allRooms: "Всички",
    roomsSuffix: "стаи",
    areaLabel: "Площ (м²)",
    priceLabel: "Цена (€)",
    pricePerSqmLabel: "Цена за м² (€)",
    from: "От",
    to: "До",
    minPrice: "Мин. цена",
    maxPrice: "Макс. цена",
    minArea: "Мин. м²",
    maxArea: "Макс. м²",
    searchButton: "Търсене",
    reset: "Изчисти",
    noResults: "Няма намерени имоти",
    showing: "Показано",
    of: "от",
    sortLabel: "Сортиране:",
    sortBest: "Най-добрите",
    sortNew: "Нови",
    sortMore: "Сортирай по...",
    sortPriceAsc: "Цена ↑",
    sortPriceDesc: "Цена ↓",
    sortAiValuation: "AI оценка",
    sortArea: "Площад",
    sortPricePerSqm: "Цена/м²",
    sortHot: "Горещи оферти 🔥",
    sortHotTooltip: "Достъпно само за Апартаменти",
    advancedFilters: "Допълнителни филтри",
    advancedActive: "Активни",
    floorLabel: "Етаж",
    constructionYearLabel: "Година на строителство",
    buildingMaterialsLabel: "Вид строителство",
    buildingMaterialsAll: "Всички",
    gasLabel: "Газ",
    gasOnly: "Само с газ",
    centralHeatingLabel: "ТЕЦ",
    centralHeatingOnly: "Само с ТЕЦ",
    selectApartments: 'Изберете тип "Апартаменти"',
  },
  propertyDetail: {
    detailedAnalysis: "Детайлен анализ",
    getDetailedAnalysis: "Получете детайлен анализ — €5",
    backToSearch: "Назад към търсенето",
    notFound: "Имотът не е намерен",
    description: "Описание",
    characteristics: "Характеристики",
    pricePerSqm: "Цена/м²",
    area: "Площ",
    bedrooms: "Стаи",
    propertyType: "Тип имот",
    floor: "Етаж",
    construction: "Строителство",
    buildingMaterials: {
      brick: "Тухла",
      panel: "Панел",
      monolith: "Монолит",
      epk: "ЕПК",
      other: "Друго",
    },
    daysOnMarket: "Публикувана",
    today: "Днес",
    day: "ден",
    days: "дни",
    viewOriginal: "Виж в източника",
    save: "Запази",
    potentialSavings: "Потенциални спестявания",
    listingPrice: "Цена в обявата",
    aiValuation: "AI Оценка",
    priceDeviation: "Ценово отклонение",
    similarProperties: "Подобни имоти",
    utilityCost: "Комунални разходи",
    priceComparison: "спрямо район",
    vsAvg: "спрямо средна",
    priceTrend: "Ценова тенденция в района",
    last6Months: "Последни 6 месеца",
    districtAvg: "Средна за района",
    location: "Местоположение",
    openMaps: "Отвори в Maps",
    noLocation: "Няма местоположение",
    contact: "Контакт",
    ownerContacts: "Контакти на собственика",
    name: "Име",
    phone: "Телефон",
    email: "Имейл",
    district: "Район",
    noContactInfo: "Няма налична информация за контакт",
    moreDetails: "Подробности",
    districtRatings: {
      traffic: "Трафик",
      publicTransport: "Обществен транспорт",
      shops: "Магазини",
      schools: "Училища",
    },
  },
  propertyCard: {
    aiEstimate: "AI оценка",
    goodDeal: "Изгодна цена",
    overpriced: "Завишена цена",
    belowMarket: "под пазарната",
    aboveMarket: "над пазарната",
    fairPrice: "Справедлива цена",
    significantlyBelow: "Значително под пазарната цена",
    significantlyAbove: "Значително над пазарната цена",
    rooms: "стаи",
    types: {
      apartments: "Апартамент",
      house: "Къща",
      studio: "Студио",
      garage: "Гараж",
      land: "Парцел",
      commercial: "Търговски обект",
      other: "Имот",
    },
  },
  pricing: {
    title: "Тарифи",
    subtitle: "Изберете плана, който отговаря на вашите нужди",
    free: {
      name: "Безплатно",
      price: "€0",
      period: "",
      description: "Базов достъп до платформата",
      features: ["Търсене на имоти", "Базови обяви", "Общ Market Score"],
      cta: "Започнете",
    },
    perProperty: {
      name: "Детайлен анализ",
      price: "€5",
      period: "за имот",
      description: "Еднократен задълбочен отчет за конкретен имот",
      features: ["Пълна AI оценка", "Сравнение цена vs. пазар", "Анализ на квартала", "Експорт в PDF"],
      cta: "Купи анализ",
    },
    pro: {
      name: "Pro",
      price: "€59",
      period: "/месец",
      description: "За агенти и малки агенции",
      features: ["Неограничени отчети", "Умни известия", "Експорт на данни", "Приоритетна поддръжка", "API достъп"],
      cta: "Заявка",
    },
    enterprise: {
      name: "Enterprise",
      price: "По договаряне",
      period: "",
      description: "За предприемачи, фондове и големи агенции",
      features: [
        "Персонализирани интеграции",
        "Личен мениджър",
        "Масов достъп до данни",
        "White-label опции",
        "SLA гаранция",
      ],
      cta: "Свържете се с нас",
    },
    popular: "Най-популярен",
  },
  dataBar: {
    listings: "Анализирани обяви",
    neighborhoods: "Оценени квартали",
    updated: "Обновява се ежедневно",
    platform: "Платформа в ЕС",
  },
  howItWorksNew: {
    title: "Как работи",
    subtitle: "От търсене до решение в 3 стъпки",
    step1Title: "Намерете имот",
    step1Desc:
      "Търсене из всички големи български сайтове за обяви на едно място. Филтрирайте по град, цена, тип и други.",
    step2Title: "Получете AI оценка",
    step2Desc:
      "Моментална оценка на пазарната стойност, сравнение на цените с подобни имоти, анализ на квартала — всичко в един отчет.",
    step3Title: "Вземете решение на база данни",
    step3Desc: "Разберете дали надплащате. Вижте реалната пазарна картина преди да преговаряте.",
  },
  productFeatures: {
    title: "Създадено за всички в недвижимите имоти",
    subtitle: "Независимо дали купувате първия си апартамент или управлявате портфолио от 500 имота",
    buyers: {
      title: "За купувачи и инвеститори",
      features: [
        "Безплатно търсене на имоти в цяла България",
        "AI оценка за €5 на имот",
        "Market Score за всяка обява",
        "Графики на ценови тренд и аналози",
        "Данни за безопасност и инфраструктура на квартала",
      ],
      cta: "Започни безплатно",
    },
    agents: {
      title: "За агенти и агенции",
      features: [
        "Неограничени AI отчети с Pro план",
        "PDF експорт за клиенти",
        "Сравнителен пазарен анализ",
        "Умни известия за нови обяви",
        "Приоритетна поддръжка",
      ],
      cta: "Пробвай Pro безплатно 14 дни",
    },
    enterprise: {
      title: "За Enterprise",
      features: [
        "API достъп за масови оценки",
        "White-label отчети с ваш бранд",
        "Персонализирани интеграции на данни",
        "Персонален мениджър",
        "SLA гаранция",
      ],
      cta: "Свържете се с нас",
    },
  },
  pricingTeaser: {
    title: "Прости и прозрачни цени",
    subtitle: "Започнете безплатно. Плащайте само когато имате нужда от повече.",
    free: { name: "Безплатен", desc: "Търсене и базови данни", cta: "Започни безплатно" },
    report: { name: "AI Отчет", suffix: "/ имот", desc: "Пълна оценка + пазарен анализ", cta: "Опитай" },
    pro: { suffix: "/ месец", desc: "Неограничени отчети + умни инструменти", cta: "Започни Pro пробен" },
    enterprise: "Enterprise? Нека поговорим.",
    enterpriseLink: "Свържете се с нас за персонализиран API, white-label и решения за данни.",
  },
  faq: {
    title: "Често задавани въпроси",
    subtitle: "Всичко, което трябва да знаете за Dealigator",
    items: [
      {
        q: "Какви данни използва Dealigator?",
        a: "Агрегираме обяви от всички големи български платформи за недвижими имоти, комбинирайки ги с данни за кварталите, история на сделките и собствени AI модели за оценка на пазарната стойност.",
      },
      {
        q: "Колко точна е AI оценката?",
        a: "Моделът е обучен на исторически данни за сделки и постоянно се актуализира. Dealigator предоставя надеждна пазарна оценка за вземане на информирани решения.",
      },
      {
        q: "Трябва ли да създам акаунт?",
        a: "Базовото търсене е достъпно без регистрация. За закупуване на AI отчети или абонамент за Pro е необходим безплатен акаунт.",
      },
      {
        q: "Данните ми в безопасност ли са?",
        a: "Да. Ние сме платформа в ЕС, напълно съответстваща на GDPR. Не споделяме данните ви с трети страни.",
      },
      {
        q: "Аз съм брокер. Има ли план за агенции?",
        a: "Да! Pro планът е идеален за агенти, а Enterprise предлага API достъп, white-label отчети и групови цени за агенции.",
      },
    ],
  },
  finalCta: {
    title: "Готови ли сте да видите реалния пазар?",
    subtitle: "Започнете с безплатно търсене. Получете първата AI оценка за €5.",
  },
  servicePage: {
    pageTitle: "Услуги",
    backToServices: "Обратно към услугите",
    details: "Подробности",
    order: "Поръчай",
    consultation: "Консултация",
    addToCart: "Добави в кошницата",
    alreadyInCart: "Вече е в кошницата",
    addedToCart: "Добавено в кошницата",
    mostPopular: "Най-популярна",
    cart: "Кошница",
    cartEmpty: "Кошницата е празна",
    browseServices: "Разгледай услугите",
    name: "Име *",
    email: "Имейл *",
    phone: "Телефон",
    notes: "Бележки",
    paymentNote: "* Заплащането се извършва след обсъждане на детайлите с нашия екип.",
    confirmOrder: "Потвърди поръчката",
    cancel: "Отказ",
    orderAccepted: "Поръчката е приета!",
    orderAcceptedDesc: "Ще се свържем с вас по електронна поща в най-кратък срок за обсъждане на детайлите.",
    ok: "Добре",
    fillNameEmail: "Моля, попълнете име и имейл",
    orderError: "Грешка при поръчката",
    waitSecurity: "Моля, изчакайте проверката за сигурност",
    cartOrderNote: "Поръчка от кошницата",
    consultationBtn: "Консултация по услугите",
    consultationTitle: "Консултация по услугите",
    consultationName: "Име *",
    consultationEmail: "Имейл *",
    consultationPhone: "Телефон",
    consultationMessage: "Вашият въпрос *",
    consultationSubmit: "Изпратете",
    consultationSuccess: "Успешно!",
    consultationSuccessDesc: "Ще се свържем с вас скоро.",
    consultationError: "Грешка",
    consultationErrorDesc: "Моля, опитайте отново.",
  },
};

const ru = {
  nav: {
    home: "Главная",
    deals: "Поиск",
    pricing: "Тарифы",
    forAgents: "Для агентов",
    blog: "Блог",
    analytics: "Аналитика",
    howItWorks: "Как это работает",
    signIn: "Вход",
    getStarted: "Начать",
    startFree: "Начать бесплатно",
    dashboard: "Панель",
    myFilters: "Мои фильтры",
    myAlerts: "Мои уведомления",
    signOut: "Выход",
    favorites: "Избранное",
  },
  hero: {
    title: "Узнайте реальную стоимость любой квартиры в Болгарии",
    subtitle: "ИИ анализирует 50 000+ объявлений: мгновенная оценка, сравнение с рынком, оценка районов.",
    ctaPrimary: "Начать бесплатно",
    ctaSecondary: "Тарифы",
    trustLine: "Без кредитной карты • Бесплатный поиск навсегда",
    stats: {
      properties: "Проанализировано объектов",
      avgSavings: "Средняя экономия на сделке благодаря нашему алгоритму",
      activeAlerts: "Активных уведомлений",
      citiesCovered: "Покрыто городов",
    },
    banners: {
      banner1Title: "Боитесь переплатить за квартиру в Болгарии?",
      banner1Description:
        "Мы бесплатно посчитаем сколько на самом деле стоит квадратный метр в выбранном вами районе или городе",
      banner1Bonus: "Также вы получите скидку -20% на любую нашу услугу",
      banner1Cta: "Рассчитать",
      banner2Title: "Инвестируете в болгарскую недвижимость?",
      banner2Description:
        "Бесплатно получите закрытую аналитику: тепловую карту стоимости м² и реальный годовой прирост цен (ROI) по районам Софии или побережья",
      banner2Bonus: "Также вы получите скидку -20% на любую нашу услугу",
      banner2Cta: "Получить аналитику",
    },
    mockCard: {
      location: "Лозенец, София",
      price: "€128 000",
      area: "78 м²",
      pricePerSqm: "€1 641/м²",
      aiValuation: "Оценка ИИ",
      belowMarket: "12% ниже рынка",
      marketScore: "Рейтинг района",
    },
  },
  marketProblem: {
    title: "Рынок работает против покупателя. Мы меняем правила.",
    description:
      "Обычные брокеры защищают продавца или свою комиссию. Dealigator работает только на вас — мы снижаем цену и гарантируем чистоту сделки.",
    standard: {
      title: "Стандарт",
      seller: "Продавец",
      sellerBroker: "Брокер продавца",
      wantHighPrice: "Оба хотят высокую цену",
      vs: "VS",
      lonelyBuyer: "Одинокий покупатель",
    },
    dealigator: {
      title: "С Dealigator",
      buyer: "Покупатель",
      aiLawyerNegotiator: "AI + Юрист + Переговорщик",
      fullProtection: "Полная защита ваших интересов",
      vs: "VS",
      seller: "Продавец",
    },
  },
  secretWeapon: {
    title: "Мы знаем реальную цену до переговоров.",
    description: "Наш алгоритм анализирует 50+ факторов и оценивает объект с точностью 97%.",
    benefit:
      'Как это вам помогает? Вы идёте на просмотр с "козырем в рукаве" — точно знаете, сколько стоит жильё, и имеете аргументы для снижения цены.',
  },
  services: {
    title: "Полная защита от поиска до ключа",
    aiAnalysis: {
      title: "AI Оценка и Анализ",
      description:
        "Находим недооценённые объекты и показываем их реальную стоимость, скрытые риски и потенциал аренды.",
    },
    negotiation: {
      title: "Переговоры и Сделка",
      description:
        "Наш эксперт ведёт переговоры за вас. Цель: снизить цену на 10-15%. Если не снизим — услуга бесплатна.",
    },
    legal: {
      title: "Юридическая безопасность",
      description: "Проверяем объект на обременения, готовим договоры и защищаем ваши деньги на эскроу-счёте.",
    },
  },
  battle: {
    title: "Почему выбирают Dealigator?",
    broker: "Обычный брокер",
    dealigator: "Dealigator",
    interest: { label: "Чьи интересы?", broker: "Продавца / Свои", ai: "Только Ваши (Покупателя)" },
    priceEval: { label: "Оценка цены", broker: '"На глаз" / Со слов продавца', ai: "AI Алгоритм (97% точность)" },
    goalNeg: { label: "Цель переговоров", broker: "Быстрая сделка", ai: "Максимальная скидка" },
    legalCheck: { label: "Юр. проверка", broker: "Поверхностная", ai: "Глубокая (Обременения, история)" },
    pricing: {
      label: "Цена",
      broker: "3-6% комиссия",
      ai: "2.5% или фиксированная комиссия (в зависимости от услуги)",
    },
  },
  featuredDeals: {
    badge: "Новое",
    title: "Последние объявления",
    subtitle: "Будьте первыми, кто их увидит.",
    viewAll: "Все объявления",
    belowMarket: "ниже рынка",
    beds: "спален",
    baths: "ванных",
    viewDeal: "Смотреть",
  },
  analyticsPreview: {
    badge: "Умная аналитика",
    title: "Ваша персональная аналитическая платформа",
    subtitle: "Глубокий анализ рынка, прогнозы цен и анализ трендов для принятия обоснованных инвестиционных решений.",
    features: {
      priceAnalytics: {
        title: "Ценовая аналитика",
        description: "Исторические тренды и прогнозы",
      },
      heatMaps: {
        title: "Тепловые карты",
        description: "Визуализируйте горячие зоны",
      },
      roiCalculator: {
        title: "Калькулятор ROI",
        description: "Оцените доходность инвестиций",
      },
      dealAlerts: {
        title: "Уведомления о сделках",
        description: "Мгновенные уведомления о подходящих сделках",
      },
    },
    exploreButton: "Изучить аналитику",
    marketOverview: "Обзор рынка Софии",
    pricePerSqm: "Цена/м²",
    listings: "Объявлений",
    avgDays: "Средн. дней",
    newDealFound: "Новая сделка!",
    matchesFilters: "Соответствует вашим фильтрам",
  },
  howItWorks: {
    badge: "Простой процесс",
    title: "Как работает Dealigator",
    subtitle: "Три простых шага к идеальной сделке с недвижимостью.",
    steps: {
      collect: {
        step: "01",
        title: "Мы собираем данные",
        description: "Наша система агрегирует объявления из 20+ источников по Болгарии, обновляя каждый час.",
      },
      analyze: {
        step: "02",
        title: "AI анализирует",
        description:
          "Модели машинного обучения оценивают каждый объект по рыночным данным для поиска недооценённых находок.",
      },
      alert: {
        step: "03",
        title: "Вы получаете уведомление",
        description:
          "Получайте мгновенные уведомления, когда объекты по вашим критериям появляются ниже рыночной стоимости.",
      },
    },
  },
  footer: {
    cta: {
      title: "Готовы к следующей сделке?",
      subtitle:
        "Присоединяйтесь к тысячам инвесторов, которые уже используют Dealigator для поиска недооценённой недвижимости.",
      emailPlaceholder: "Введите email",
      button: "Начать бесплатно",
    },
    brand: {
      description:
        "Умный способ покупки недвижимости. AI-оценка, юридическая защита и профессиональные переговоры по цене. Мы работаем только на вас, а не на продавца.",
    },
    platform: {
      title: "Платформа",
      search: "Поиск сделок",
      analytics: "Аналитика",
      alerts: "Ценовые уведомления",
      api: "API доступ",
    },
    resources: {
      title: "Ресурсы",
      blog: "Блог",
      guides: "Рыночные гиды",
      support: "Поддержка",
      pricing: "Цены",
    },
    contact: {
      title: "Контакты",
      location: "София, Болгария",
      feedback: "Форма обратной связи",
    },
    legal: {
      rights: "Все права защищены.",
      privacy: "Политика конфиденциальности",
      terms: "Условия использования",
    },
  },
  contact: {
    back: "Назад",
    title: "Свяжитесь с нами",
    subtitle: "Мы будем рады услышать вас. Отправьте нам сообщение, и мы ответим как можно скорее.",
    formTitle: "Отправить сообщение",
    formDescription: "Заполните форму ниже, и мы свяжемся с вами в ближайшее время.",
    name: "Имя",
    namePlaceholder: "Ваше имя",
    email: "Email",
    emailPlaceholder: "your@email.com",
    subject: "Тема",
    subjectPlaceholder: "Чем мы можем помочь?",
    message: "Сообщение",
    messagePlaceholder: "Расскажите подробнее о вашем запросе...",
    submit: "Отправить сообщение",
    sending: "Отправка...",
    successTitle: "Сообщение отправлено",
    successDescription: "Спасибо за ваше сообщение. Мы свяжемся с вами в ближайшее время!",
    errors: {
      nameMin: "Имя должно быть не менее 2 символов",
      nameMax: "Имя должно быть менее 100 символов",
      invalidEmail: "Пожалуйста, введите корректный email",
      emailMax: "Email должен быть менее 255 символов",
      subjectMin: "Тема должна быть не менее 3 символов",
      subjectMax: "Тема должна быть менее 200 символов",
      messageMin: "Сообщение должно быть не менее 10 символов",
      messageMax: "Сообщение должно быть менее 2000 символов",
      captchaRequired: "Требуется captcha",
      captchaDescription: "Пожалуйста, пройдите проверку captcha",
      submitFailed: "Ошибка отправки",
      tryAgain: "Пожалуйста, попробуйте позже",
    },
  },
  auth: {
    backToHome: "На главную",
    signIn: "Вход",
    signUp: "Регистрация",
    signInDesc: "Войдите для доступа к сохранённым фильтрам и уведомлениям",
    signUpDesc: "Зарегистрируйтесь для отслеживания лучших сделок",
    name: "Имя",
    namePlaceholder: "Ваше имя",
    email: "Email",
    emailPlaceholder: "email@example.com",
    password: "Пароль",
    loading: "Загрузка...",
    or: "или",
    continueWithGoogle: "Продолжить с Google",
    noAccount: "Нет аккаунта?",
    haveAccount: "Уже есть аккаунт?",
    invalidEmail: "Пожалуйста, введите корректный email",
    passwordMin: "Пароль должен быть не менее 6 символов",
    nameMin: "Имя должно быть не менее 2 символов",
    loginError: "Ошибка входа",
    invalidCredentials: "Неверный email или пароль",
    error: "Ошибка",
    welcomeBack: "С возвращением!",
    signedIn: "Успешный вход",
    userExists: "Пользователь существует",
    userExistsDesc: "Этот email уже зарегистрирован. Попробуйте войти.",
    registrationError: "Ошибка регистрации",
    registrationSuccess: "Успешная регистрация",
    accountCreated: "Аккаунт создан. Добро пожаловать!",
    somethingWrong: "Что-то пошло не так. Попробуйте снова.",
    googleError: "Не удалось войти через Google",
    confirmEmail: "Подтвердите вашу почту",
    confirmEmailSent: "Мы отправили ссылку для подтверждения на",
    confirmEmailDesc: "Пожалуйста, проверьте входящие письма и нажмите на ссылку, чтобы активировать аккаунт.",
    checkSpam: 'Проверьте папку "Спам", если не видите письмо.',
    backToLogin: "Вернуться ко входу",
  },
  dashboard: {
    back: "Назад",
    signOut: "Выход",
    title: "Панель управления",
    tabs: {
      filters: "Сохранённые фильтры",
      alerts: "Уведомления",
      profile: "Профиль",
    },
    filters: {
      title: "Сохранённые фильтры",
      description: "Управляйте сохранёнными фильтрами поиска",
      create: "Создать фильтр",
      noFilters: "Нет сохранённых фильтров",
      noFiltersDesc: "Создайте первый фильтр для отслеживания недвижимости.",
      location: "Местоположение",
      priceRange: "Ценовой диапазон",
      area: "Площадь",
      type: "Тип",
      rooms: "комнат",
      edit: "Изменить",
      delete: "Удалить",
      filterName: "Название фильтра",
      filterNamePlaceholder: "напр. Квартиры в центре Софии",
      locationLabel: "Местоположение",
      locationPlaceholder: "Город или район",
      minPrice: "Мин цена (€)",
      maxPrice: "Макс цена (€)",
      minArea: "Мин площадь (м²)",
      maxArea: "Макс площадь (м²)",
      propertyType: "Тип недвижимости",
      selectType: "Выберите тип",
      apartment: "Квартира",
      house: "Дом",
      studio: "Студия",
      commercial: "Коммерческая",
      land: "Земля",
      roomsLabel: "Комнаты",
      saveFilter: "Сохранить фильтр",
      cancel: "Отмена",
    },
    alerts: {
      title: "Мои уведомления",
      description: "Настройте уведомления о новых сделках",
      create: "Создать уведомление",
      noAlerts: "Нет настроенных уведомлений",
      noAlertsDesc: "Создайте уведомление о новых сделках по вашим критериям.",
      active: "Активно",
      linkedFilter: "Связанный фильтр",
      minScore: "Мин скор сделки",
      maxDeviation: "Макс рыночное отклонение",
      notifications: "Уведомления",
      email: "Email",
      push: "Push",
      delete: "Удалить",
      alertName: "Название уведомления",
      alertNamePlaceholder: "напр. Горячие сделки в Софии",
      selectFilter: "Связанный фильтр (опционально)",
      noFilter: "Без фильтра",
      minDealScore: "Минимальный скор сделки",
      maxPriceDeviation: "Макс цена относительно рынка (%)",
      notifyEmail: "Email уведомления",
      notifyPush: "Push уведомления",
      saveAlert: "Сохранить уведомление",
      cancel: "Отмена",
    },
    profile: {
      title: "Настройки профиля",
      description: "Управляйте информацией аккаунта",
      fullName: "Полное имя",
      fullNamePlaceholder: "Ваше имя",
      email: "Email",
      emailPlaceholder: "Ваш email",
      saveChanges: "Сохранить изменения",
      statistics: "Статистика",
      savedFilters: "Сохранённых фильтров",
      activeAlerts: "Активных уведомлений",
      profileUpdated: "Профиль обновлён",
      profileUpdatedDesc: "Ваш профиль успешно обновлён.",
      updateError: "Ошибка",
      updateErrorDesc: "Не удалось обновить профиль.",
    },
    toast: {
      filterCreated: "Фильтр создан",
      filterCreatedDesc: "Ваш фильтр сохранён.",
      filterDeleted: "Фильтр удалён",
      filterDeletedDesc: "Фильтр был удалён.",
      alertCreated: "Уведомление создано",
      alertCreatedDesc: "Вы будете получать уведомления о подходящих сделках.",
      alertDeleted: "Уведомление удалено",
      alertDeletedDesc: "Уведомление было удалено.",
      alertUpdated: "Уведомление обновлено",
      alertUpdatedDesc: "Статус уведомления изменён.",
      error: "Ошибка",
      errorDesc: "Произошла ошибка. Попробуйте снова.",
    },
  },
  properties: {
    title: "Поиск недвижимости",
    subtitle: "Найдите лучшие предложения на рынке",
    filtersTitle: "Фильтры поиска",
    searchLabel: "Поиск по ключевым словам",
    searchPlaceholder: "Район, описание, адрес...",
    cityLabel: "Город",
    cityPlaceholder: "Город",
    allCities: "Все города",
    propertyTypeLabel: "Тип жилья",
    allTypes: "Все типы",
    roomsLabel: "Количество комнат",
    allRooms: "Любое",
    roomsSuffix: "комн.",
    areaLabel: "Площадь (м²)",
    priceLabel: "Цена (€)",
    pricePerSqmLabel: "Цена за м² (€)",
    from: "От",
    to: "До",
    minPrice: "Мин. цена",
    maxPrice: "Макс. цена",
    minArea: "Мин. м²",
    maxArea: "Макс. м²",
    searchButton: "Поиск",
    reset: "Сбросить",
    noResults: "Ничего не найдено",
    showing: "Показано",
    of: "из",
    sortLabel: "Сортировка:",
    sortBest: "Лучшие",
    sortNew: "Новые",
    sortMore: "Сортировка",
    sortPriceAsc: "Цена ↑",
    sortPriceDesc: "Цена ↓",
    sortAiValuation: "AI оценка",
    sortArea: "Площадь",
    sortPricePerSqm: "Цена/м²",
    sortHot: "Горячие предложения 🔥",
    sortHotTooltip: "Доступно только для Апартаментов",
    advancedFilters: "Дополнительные фильтры",
    advancedActive: "Активны",
    floorLabel: "Этаж",
    constructionYearLabel: "Год постройки",
    buildingMaterialsLabel: "Тип строительства",
    buildingMaterialsAll: "Все",
    gasLabel: "Газ",
    gasOnly: "Только с газом",
    centralHeatingLabel: "Центр. отопление",
    centralHeatingOnly: "Только с центр. отоплением",
    selectApartments: 'Выберите тип "Апартаменти"',
  },
  propertyDetail: {
    detailedAnalysis: "Детальный анализ",
    getDetailedAnalysis: "Получить детальный анализ — €5",
    backToSearch: "Назад к поиску",
    notFound: "Объект не найден",
    description: "Описание",
    characteristics: "Характеристики",
    pricePerSqm: "Цена/м²",
    area: "Площадь",
    bedrooms: "Комнаты",
    propertyType: "Тип недвижимости",
    floor: "Этаж",
    construction: "Строительство",
    buildingMaterials: {
      brick: "Кирпич",
      panel: "Панель",
      monolith: "Монолит",
      epk: "ЕПК",
      other: "Другое",
    },
    daysOnMarket: "Дней на рынке",
    today: "Сегодня",
    day: "день",
    days: "дней",
    viewOriginal: "Смотреть на источнике",
    save: "Сохранить",
    potentialSavings: "Потенциальная экономия",
    listingPrice: "Цена объявления",
    aiValuation: "AI Оценка",
    priceDeviation: "Отклонение цены",
    similarProperties: "Похожие объекты",
    utilityCost: "Коммунальные расходы",
    priceComparison: "vs средняя района",
    vsAvg: "vs средней",
    priceTrend: "Динамика цен в районе",
    last6Months: "Последние 6 месяцев",
    districtAvg: "Средняя по району",
    location: "Расположение",
    openMaps: "Открыть в Maps",
    noLocation: "Местоположение недоступно",
    contact: "Контакт",
    ownerContacts: "Контакты владельца",
    name: "Имя",
    phone: "Телефон",
    email: "Email",
    district: "Район",
    noContactInfo: "Контактная информация недоступна",
    moreDetails: "Подробнее",
    districtRatings: {
      traffic: "Трафик",
      publicTransport: "Общественный транспорт",
      shops: "Магазины",
      schools: "Школы",
    },
  },
  propertyCard: {
    aiEstimate: "AI оценка",
    goodDeal: "Выгодная цена",
    overpriced: "Завышенная цена",
    belowMarket: "ниже рынка",
    aboveMarket: "выше рынка",
    fairPrice: "рыночная цена",
    significantlyBelow: "Значительно ниже рыночной цены",
    significantlyAbove: "Значительно выше рыночной цены",
    rooms: "комнат",
    types: {
      apartments: "Квартира",
      house: "Дом",
      studio: "Студия",
      garage: "Гараж",
      land: "Участок",
      commercial: "Коммерческая",
      other: "Недвижимость",
    },
  },
  pricing: {
    title: "Тарифы",
    subtitle: "Выберите план, который подходит вам",
    free: {
      name: "Бесплатно",
      price: "€0",
      period: "",
      description: "Базовый доступ к платформе",
      features: ["Поиск недвижимости", "Базовые объявления", "Общий Market Score"],
      cta: "Начать",
    },
    perProperty: {
      name: "Детальный анализ",
      price: "€5",
      period: "за объект",
      description: "Разовый подробный отчёт по конкретному объекту",
      features: ["Полная AI-оценка", "Сравнение цены с рынком", "Аналитика района", "Экспорт в PDF"],
      cta: "Купить анализ",
    },
    pro: {
      name: "Pro",
      price: "€59",
      period: "/месяц",
      description: "Для агентов и небольших агентств",
      features: [
        "Неограниченные отчёты",
        "Умные уведомления",
        "Экспорт данных",
        "Приоритетная поддержка",
        "API-доступ",
      ],
      cta: "Заявка",
    },
    enterprise: {
      name: "Enterprise",
      price: "Индивидуально",
      period: "",
      description: "Для девелоперов, фондов и крупных агентств",
      features: [
        "Кастомные интеграции",
        "Персональный менеджер",
        "Массовый доступ к данным",
        "White-label",
        "SLA-гарантия",
      ],
      cta: "Свяжитесь с нами",
    },
    popular: "Самый популярный",
  },
  dataBar: {
    listings: "Объявлений проанализировано",
    neighborhoods: "Районов оценено",
    updated: "Обновляется ежедневно",
    platform: "Платформа в ЕС",
  },
  howItWorksNew: {
    title: "Как это работает",
    subtitle: "От поиска до решения за 3 шага",
    step1Title: "Найдите объект",
    step1Desc:
      "Поиск по всем крупным площадкам Болгарии в одном месте. Фильтры по городу, цене, типу и другим параметрам.",
    step2Title: "Получите AI-оценку",
    step2Desc: "Рыночная стоимость, сравнение цен, аналитика района — в одном отчёте.",
    step3Title: "Примите решение на основе данных",
    step3Desc: "Узнайте, не переплачиваете ли вы. Взгляните на реальную картину рынка до начала сделки.",
  },
  productFeatures: {
    title: "Создано для всех в недвижимости",
    subtitle: "Покупаете первую квартиру или управляете портфелем из 500 объектов",
    buyers: {
      title: "Для покупателей и инвесторов",
      features: [
        "Бесплатный поиск по всей Болгарии",
        "AI-оценка за €5 за объект",
        "Market Score для каждого объявления",
        "Графики и сравнения",
        "Данные по районам",
      ],
      cta: "Начать бесплатно",
    },
    agents: {
      title: "Для агентов и агентств",
      features: [
        "Безлимитные AI-отчёты на тарифе Pro",
        "Экспорт PDF для клиентов",
        "Сравнительный анализ рынка",
        "Умные уведомления",
        "Приоритетная поддержка",
      ],
      cta: "Попробуйте Pro бесплатно",
    },
    enterprise: {
      title: "Для Enterprise",
      features: [
        "API-доступ для массовых оценок",
        "White-label отчёты с вашим брендингом",
        "Кастомные интеграции данных",
        "Персональный менеджер",
        "SLA-гарантия",
      ],
      cta: "Свяжитесь с нами",
    },
  },
  pricingTeaser: {
    title: "Понятные и прозрачные тарифы",
    subtitle: "Начните бесплатно. Платите только когда нужно больше.",
    free: { name: "Бесплатно", desc: "Поиск и базовые данные", cta: "Начать бесплатно" },
    report: { name: "AI-отчёт", suffix: "/ объект", desc: "Полная оценка + анализ рынка", cta: "Попробовать" },
    pro: { suffix: "/ месяц", desc: "Безлимитные отчёты + инструменты", cta: "Попробовать" },
    enterprise: "Enterprise? Давайте обсудим.",
    enterpriseLink: "Свяжитесь с нами для API, white-label и решений для массовых данных.",
  },
  faq: {
    title: "Часто задаваемые вопросы",
    subtitle: "Всё, что нужно знать о Dealigator",
    items: [
      {
        q: "Какие данные использует Dealigator?",
        a: "Мы агрегируем объявления со всех крупных площадок Болгарии, объединяя их с данными по районам, историей сделок и собственными AI-моделями.",
      },
      {
        q: "Насколько точна AI-оценка?",
        a: "Модель обучена на исторических данных о сделках и постоянно обновляется. Это надёжная рыночная оценка для принятия обоснованных решений.",
      },
      {
        q: "Нужно ли создавать аккаунт?",
        a: "Базовый поиск доступен без регистрации. Для покупки AI-отчётов или подписки Pro нужен бесплатный аккаунт.",
      },
      {
        q: "Мои данные в безопасности?",
        a: "Да. Мы — платформа в ЕС, полностью соответствуем GDPR. Мы не передаём данные третьим лицам.",
      },
      {
        q: "Я риелтор. Есть тариф для агентств?",
        a: "Да! Pro идеален для агентов, а Enterprise предлагает API, white-label и оптовые цены. Свяжитесь с нами.",
      },
    ],
  },
  finalCta: {
    title: "Готовы увидеть реальный рынок?",
    subtitle: "Начните с бесплатного поиска. Получите первую AI-оценку за €5.",
  },
  servicePage: {
    pageTitle: "Услуги",
    backToServices: "Назад к услугам",
    details: "Подробнее",
    order: "Заказать",
    consultation: "Консультация",
    addToCart: "В корзину",
    alreadyInCart: "Уже в корзине",
    addedToCart: "Добавлено в корзину",
    mostPopular: "Самая популярная",
    cart: "Корзина",
    cartEmpty: "Корзина пуста",
    browseServices: "Посмотреть услуги",
    name: "Имя *",
    email: "Эл. почта *",
    phone: "Телефон",
    notes: "Примечания",
    paymentNote: "* Оплата производится после обсуждения деталей с нашей командой.",
    confirmOrder: "Подтвердить заказ",
    cancel: "Отмена",
    orderAccepted: "Заказ принят!",
    orderAcceptedDesc: "Мы свяжемся с вами по электронной почте в ближайшее время для обсуждения деталей.",
    ok: "Хорошо",
    fillNameEmail: "Пожалуйста, заполните имя и email",
    orderError: "Ошибка при оформлении заказа",
    waitSecurity: "Пожалуйста, дождитесь проверки безопасности",
    cartOrderNote: "Заказ из корзины",
    consultationBtn: "Консультация по услугам",
    consultationTitle: "Консультация по услугам",
    consultationName: "Имя *",
    consultationEmail: "Эл. почта *",
    consultationPhone: "Телефон",
    consultationMessage: "Ваш вопрос *",
    consultationSubmit: "Отправить",
    consultationSuccess: "Успешно!",
    consultationSuccessDesc: "Мы свяжемся с вами в ближайшее время.",
    consultationError: "Ошибка",
    consultationErrorDesc: "Пожалуйста, попробуйте снова.",
  },
};

export const translations = {
  en,
  bg,
  ru,
} as const;

export type TranslationKeys = typeof en;
